import axios from 'axios';
import CbError from '@/utils/cb-error';
import { logErrorWithBackoff } from '@/utils/error-logging-adapter';
import type { LogErrorRequest, ErrorMeta, SanitizedAxiosCause, SanitizedCbErrorCause } from '@/utils/error-logging-types';

const SOURCE_NAME = 'cb-checkout-v3';
const SOURCE_VERSION =
  (typeof process !== 'undefined' && process.env && process.env.CB_BUILD_TAG) || '0.0.0';

/**
 * Sanitize axios error: only safe, minimal fields. No full config (avoids noise and depth truncation in logs).
 */
function sanitizeAxiosCause(cause: import('axios').AxiosError): SanitizedAxiosCause {
  const { config, response, message, name, code } = cause;
  const status = cause.response?.status;
  let requestMeta: { url?: string; method?: string; baseURL?: string } | undefined;
  if (config && typeof config === 'object') {
    const c = config as { url?: string; method?: string; baseURL?: string };
    requestMeta = {
      ...(c.url !== undefined && { url: c.url }),
      ...(c.method !== undefined && { method: c.method }),
      ...(c.baseURL !== undefined && { baseURL: c.baseURL }),
    };
    if (Object.keys(requestMeta).length === 0) requestMeta = undefined;
  }
  return {
    message,
    name,
    code,
    status,
    config: requestMeta,
    response: response ? { status: response.status, statusText: response.statusText } : undefined,
  };
}

/**
 * Sanitize CbError: expose only safe fields (status, type, message, param). No _source, paramMessage, etc.
 */
function sanitizeCbErrorCause(cause: CbError): SanitizedCbErrorCause {
  return {
    status: cause.status,
    message: cause.message,
    type: cause.type,
    param: (cause as { paramName?: string }).paramName,
  };
}

/**
 * Sanitize plain Error: only message, name, stack (avoid passing through custom enumerable properties that may contain PII).
 */
function sanitizePlainErrorCause(cause: Error): { message?: string; name?: string; stack?: string } {
  return {
    ...(cause.message !== undefined && { message: cause.message }),
    ...(cause.name !== undefined && { name: cause.name }),
    ...(cause.stack !== undefined && { stack: cause.stack }),
  };
}

/**
 * Sanitize error for logging: strip PII from axios config, expose only safe CbError fields,
 * plain Error → { message, name, stack }, primitives → { value: String(cause) }.
 * Unknown objects → { type, keys } only (never pass through as-is to avoid PII leakage).
 */
function sanitizeErrorCause(cause: unknown): unknown {
  if (axios.isAxiosError && axios.isAxiosError(cause)) {
    return sanitizeAxiosCause(cause);
  }
  if (cause instanceof CbError) {
    return sanitizeCbErrorCause(cause);
  }
  if (cause instanceof Error) {
    return sanitizePlainErrorCause(cause);
  }
  if (typeof cause !== 'object' || cause === null) {
    return { value: String(cause) };
  }
  return {
    type: typeof cause,
    keys: Object.keys(cause as Record<string, unknown>).slice(0, 10),
  };
}

/**
 * Build LogErrorRequest and send to hp-service.
 */
export function logError(
  code: string,
  payload: ErrorMeta,
  cause?: unknown
): void {
  const stackTrace = cause instanceof Error ? cause.stack : undefined;
  const payloadObj = payload && typeof payload === 'object' && payload !== null ? payload : {};
  const hasPayloadKeys = Object.keys(payloadObj).length > 0;
  const sanitizedCause = cause != null ? sanitizeErrorCause(cause) : undefined;
  const meta: ErrorMeta = {
    ...(hasPayloadKeys ? payloadObj : {}),
    ...(sanitizedCause != null ? { cause: sanitizedCause } : {}),
  };

  const trackingPayload: LogErrorRequest = {
    error_code: code,
    ...(stackTrace ? { stack_trace: stackTrace } : {}),
    ...(Object.keys(meta).length > 0 ? { meta } : {}),
    source: { name: SOURCE_NAME, version: SOURCE_VERSION },
  };

  logErrorWithBackoff(trackingPayload);
}
